import React, { useState, useEffect, useRef } from 'react';
import { Activity, Zap, Gauge, AlertCircle, WifiOff } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { fmt } from '../../utils/safeDate';
import MachineTile from './MachineTile';
import MachineDetail from './MachineDetail';
import dataService from '../../services/dataService';
import { isStale } from '../../utils/staleness';

// Bucket fleet readings into 30s buckets, summing totalAmps across devices per bucket.
const buildFleetTrend = (readings, bucketMs = 30000) => {
  if (!readings || readings.length === 0) return [];
  const buckets = new Map();
  readings.forEach((r) => {
    const ts = r.timestamp;
    if (!ts) return;
    const key = Math.floor(ts / bucketMs) * bucketMs;
    if (!buckets.has(key)) buckets.set(key, new Map());
    // Keep most recent reading per device within each bucket (avoid double-counting)
    const perDevice = buckets.get(key);
    perDevice.set(r.machineId || r.deviceId || 'unknown', Number(r.totalAmps) || 0);
  });
  return [...buckets.entries()]
    .sort(([a], [b]) => a - b)
    .map(([ts, perDevice]) => ({
      ts,
      time: fmt(ts, 'HH:mm:ss'),
      totalAmps: parseFloat([...perDevice.values()].reduce((s, v) => s + v, 0).toFixed(2)),
    }));
};

const DarkTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-surface-800 border border-surface-600/50 rounded-lg p-3 shadow-modal">
      <p className="text-xs text-slate-400 mb-1">{label}</p>
      {payload.map((entry, i) => (
        <p key={i} className="text-xs font-medium tabular-nums" style={{ color: entry.color }}>
          {entry.name}: {typeof entry.value === 'number' ? `${entry.value.toFixed(2)} A` : entry.value}
        </p>
      ))}
    </div>
  );
};

const Dashboard = () => {
  const [machines, setMachines] = useState([]);
  const [machineData, setMachineData] = useState({});
  const [selectedMachine, setSelectedMachine] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const subscriptionsRef = useRef([]);
  const [fleetTrend, setFleetTrend] = useState([]);

  // Refresh the fleet current trend chart from readings/ every 30s
  useEffect(() => {
    let cancelled = false;
    const refresh = async () => {
      try {
        const readings = await dataService.getFleetReadingsHistory(1);
        if (cancelled) return;
        setFleetTrend(buildFleetTrend(readings, 30000));
      } catch (e) { /* leave previous data */ }
    };
    refresh();
    const interval = setInterval(refresh, 30000);
    return () => { cancelled = true; clearInterval(interval); };
  }, []);

  useEffect(() => {
    setLoading(true);
    const unsubAll = dataService.subscribeAllMachines((data, err) => {
      if (err) { setError('Failed to connect to Firestore.'); setLoading(false); return; }
      if (!data) return;
      setMachines(data);

      // Seed machineData from the collection snapshot so tiles render immediately
      const seeded = {};
      data.forEach((m) => { seeded[m.id] = m; });
      setMachineData((prev) => ({ ...seeded, ...prev }));

      // Subscribe per-machine for live latest{} updates
      data.forEach((m) => {
        if (subscriptionsRef.current.find((s) => s.id === m.id)) return;
        const unsub = dataService.subscribeMachineState(m.id, (docData) => {
          if (docData) setMachineData((prev) => ({ ...prev, [m.id]: docData }));
        });
        subscriptionsRef.current.push({ id: m.id, unsub });
      });
      setLoading(false);
    });

    return () => {
      try { unsubAll(); } catch (e) { /* noop */ }
      subscriptionsRef.current.forEach(({ unsub }) => { try { unsub(); } catch (e) { /* noop */ } });
      subscriptionsRef.current = [];
      dataService.unsubscribeAll();
    };
  }, []);

  // Fleet stats — live only. Stale machines are excluded from every aggregate.
  const stats = (() => {
    let activeMachines = 0;
    let liveMachines = 0;
    let staleCount = 0;
    let totalAmps = 0;

    Object.values(machineData).forEach((data) => {
      if (isStale(data?.timestamp)) { staleCount++; return; }
      liveMachines++;
      const t = Number(data?.latest?.totalAmps) || 0;
      totalAmps += t;
      // Active when fleet-wide threshold met
      if (t >= 1.5) activeMachines++;
    });

    return {
      activeMachines,
      liveMachines,
      staleCount,
      totalMachines: machines.length,
      totalAmps: totalAmps.toFixed(2),
      availability: liveMachines > 0 ? ((activeMachines / liveMachines) * 100).toFixed(1) : null,
    };
  })();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-brand-500/30 border-t-brand-500 rounded-full animate-spin mx-auto mb-3" />
          <p className="text-sm text-slate-400">Connecting to machines...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {error && (
        <div className="card bg-red-500/10 border-red-500/20 p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
          <p className="text-sm text-red-300">{error}</p>
        </div>
      )}

      {/* KPI strip — current-only, no fabricated power */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="kpi-card kpi-green">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Availability</p>
              <p className="metric-lg text-emerald-400">{stats.availability != null ? `${stats.availability}%` : '—'}</p>
            </div>
            <div className="p-2 rounded-lg bg-emerald-500/10">
              <Gauge className="w-5 h-5 text-emerald-400" />
            </div>
          </div>
          <p className="text-[10px] text-slate-500 mt-2">Active / live machines</p>
        </div>

        <div className="kpi-card kpi-blue">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Active</p>
              <p className="metric-lg text-blue-400">
                {stats.activeMachines}<span className="text-lg text-slate-500">/{stats.liveMachines}</span>
              </p>
            </div>
            <div className="p-2 rounded-lg bg-blue-500/10">
              <Activity className="w-5 h-5 text-blue-400" />
            </div>
          </div>
          <p className="text-[10px] text-slate-500 mt-2">
            {stats.staleCount > 0 ? `${stats.staleCount} offline / no data` : 'All machines live'}
          </p>
        </div>

        <div className="kpi-card kpi-purple">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Fleet Current</p>
              <p className="metric-lg text-purple-400">{stats.totalAmps}<span className="text-sm text-slate-500"> A</span></p>
            </div>
            <div className="p-2 rounded-lg bg-purple-500/10">
              <Zap className="w-5 h-5 text-purple-400" />
            </div>
          </div>
          <p className="text-[10px] text-slate-500 mt-2">Sum of totalAmps across live boards</p>
        </div>

        <div className="kpi-card">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Offline</p>
              <p className="metric-lg text-slate-300">{stats.staleCount}<span className="text-lg text-slate-500">/{stats.totalMachines}</span></p>
            </div>
            <div className="p-2 rounded-lg bg-slate-500/10">
              <WifiOff className="w-5 h-5 text-slate-400" />
            </div>
          </div>
          <p className="text-[10px] text-slate-500 mt-2">No push within 2 min</p>
        </div>
      </div>

      {/* Fleet */}
      <div>
        <h2 className="section-title">Machine Fleet</h2>
        <p className="section-subtitle">
          {machines.length} machine{machines.length === 1 ? '' : 's'} &bull; live updates from <code className="font-mono">machines/&#123;id&#125;.latest</code>
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {machines.map((machine) => (
          <MachineTile
            key={machine.id}
            machine={machine}
            data={machineData[machine.id]}
            onClick={() => setSelectedMachine(machine)}
          />
        ))}
      </div>

      {/* Fleet current trend — last 60 min from readings/, 30s buckets */}
      <div className="card p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-sm font-semibold text-slate-200">Fleet Current — Last 60 min</h3>
            <p className="text-xs text-slate-500">
              {fleetTrend.length > 0
                ? `${fleetTrend.length} buckets · sum of totalAmps across all machines · refreshes every 30s`
                : 'Waiting for readings…'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse-status" />
            <span className="text-xs text-slate-500">Live</span>
          </div>
        </div>
        {fleetTrend.length > 0 ? (
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={fleetTrend}>
              <defs>
                <linearGradient id="fleetCurrentGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#06b6d4" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="#06b6d4" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(30,42,69,0.5)" />
              <XAxis dataKey="time" tick={{ fill: '#64748b', fontSize: 10 }} axisLine={{ stroke: '#1e2a45' }} tickLine={false} interval="preserveStartEnd" />
              <YAxis tick={{ fill: '#64748b', fontSize: 10 }} axisLine={{ stroke: '#1e2a45' }} tickLine={false} label={{ value: 'A (fleet sum)', angle: -90, position: 'insideLeft', fill: '#64748b', fontSize: 10 }} />
              <Tooltip content={<DarkTooltip />} />
              <Area type="monotone" dataKey="totalAmps" stroke="#06b6d4" fill="url(#fleetCurrentGradient)" strokeWidth={2} name="Fleet Current (A)" dot={false} />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <div className="flex items-center justify-center h-[200px] text-slate-500 text-xs">No readings in the last hour</div>
        )}
      </div>

      {selectedMachine && (
        <MachineDetail
          machine={selectedMachine}
          data={machineData[selectedMachine.id]}
          onClose={() => setSelectedMachine(null)}
        />
      )}
    </div>
  );
};

export default Dashboard;
